import {Button, HStack} from "@chakra-ui/react";
import type {TeamId, UserMin} from "platformed-client/types";
import {useCallback, useState} from "react";
import {withDialog} from "../../state/withDialog";
import {UserPicker, UserPickerValue} from "../../components/UserPicker";
import api from "../../api";
import {usePromiseState} from "../../hooks/promiseState";
import {useDialogContext} from "../../components/dialogs";
import {inviteUserDialog} from ".";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog";
import T from "platformed-components/i18n/T";

const AddTeamMemberDialog = withDialog(({onClose, teamId}: {onClose: () => void; teamId: TeamId}) => {
  const {open} = useDialogContext();
  const [userOrInvite, setUserOrInvite] = useState<UserPickerValue | undefined>({type: "Invite", nameOrEmail: ""});

  const addUser = useCallback(
    async (user: UserMin) => {
      await api.teams.addMember(teamId, user.user_id);
    },
    [teamId],
  );
  const [adding, add] = usePromiseState(async () => {
    if (userOrInvite?.type === "User") {
      await addUser(userOrInvite.user);
    } else if (userOrInvite?.type === "Invite") {
      const initialInviteName =
        userOrInvite?.type === "Invite" && !userOrInvite.nameOrEmail.includes("@") ? userOrInvite.nameOrEmail : "";
      const initialInviteEmail =
        userOrInvite?.type === "Invite" && userOrInvite.nameOrEmail.includes("@") ? userOrInvite.nameOrEmail : "";

      setTimeout(
        () =>
          open(inviteUserDialog, {onInvite: addUser, initialName: initialInviteName, initialEmail: initialInviteEmail}),
        200,
      );
    }
    onClose();
  }, [addUser, onClose, open, userOrInvite]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T keyName="c.Add_member" />
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <UserPicker value={userOrInvite} onSelect={setUserOrInvite} onSubmit={add} autoFocus allUsers />
      </DialogBody>
      <DialogFooter>
        <HStack gap="3">
          <Button variant="outline" onClick={onClose}>
            <T keyName="c.Cancel" />
          </Button>
          <Button
            colorPalette="blue"
            onClick={add}
            disabled={!userOrInvite || adding.inProgress}
            loading={adding.inProgress}
          >
            {userOrInvite?.type === "Invite" ? <T keyName="c.Invite_and_add" /> : <T keyName="c.Add" />}
          </Button>
        </HStack>
      </DialogFooter>
    </>
  );
});

export default AddTeamMemberDialog;
